const API_BASE = import.meta.env.VITE_API_URL ?? '';

export async function patchDocument(id: string, patch: Record<string, unknown>): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/documents/${id}`, {
		method: 'PATCH',
		credentials: 'include',
		headers: { 'content-type': 'application/json', accept: 'application/json' },
		body: JSON.stringify(patch),
	});
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw Object.assign(new Error(`PATCH failed ${res.status}`), { body, status: res.status });
	}
	return res.json();
}

export async function deleteDocument(id: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/documents/${id}`, {
		method: 'DELETE',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw Object.assign(new Error(`Delete failed ${res.status}`), { body, status: res.status });
	}
	return res.json();
}

// Verplaatst een archief-document naar de inkoopfacturen-werklijst.
export async function promoteDocumentToInkoop(id: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/documents/${id}/to-inkoop`, {
		method: 'POST',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw Object.assign(new Error(`Naar inkoop failed ${res.status}`), { body, status: res.status });
	}
	return res.json();
}

export async function retryDocument(id: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/documents/${id}/retry`, {
		method: 'POST',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw Object.assign(new Error(`Retry failed ${res.status}`), { body, status: res.status });
	}
	return res.json();
}

export async function createEntity(
	params: { code: string; name: string; intake_email: string; inkoop_email?: string; verkoop_email?: string },
): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/entities`, {
		method: 'POST',
		credentials: 'include',
		headers: { 'content-type': 'application/json', accept: 'application/json' },
		body: JSON.stringify(params),
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`createEntity failed ${res.status}`), { body, status: res.status });
	return body;
}

export async function patchEntity(
	id: string,
	// inkoop_email / verkoop_email accepteren '' of null als wis-intent.
	patch: {
		name?: string;
		intake_email?: string;
		inkoop_email?: string | null;
		verkoop_email?: string | null;
		active?: boolean;
	},
): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/entities/${id}`, {
		method: 'PATCH',
		credentials: 'include',
		headers: { 'content-type': 'application/json', accept: 'application/json' },
		body: JSON.stringify(patch),
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`patchEntity failed ${res.status}`), { body, status: res.status });
	return body;
}

export async function createExactCreditor(
	entityCode: string,
	body: {
		name: string;
		address_line1?: string | null;
		postcode?: string | null;
		city?: string | null;
		chamber_of_commerce?: string | null;
		vat_number?: string | null;
		email?: string | null;
		phone?: string | null;
	},
): Promise<{ exact_id: string; name: string; code: string | null }> {
	const res = await fetch(`${API_BASE}/api/exact/creditors/${encodeURIComponent(entityCode)}/create`, {
		method: 'POST',
		credentials: 'include',
		headers: { 'content-type': 'application/json', accept: 'application/json' },
		body: JSON.stringify(body),
	});
	const respBody = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`createCreditor failed ${res.status}`), { body: respBody, status: res.status });
	return respBody as { exact_id: string; name: string; code: string | null };
}

export async function putCreditorDefaults(
	entityCode: string,
	exactId: string,
	body: {
		default_ledger_account?: string | null;
		shipping_ledger_account?: string | null;
		line_split_mode?: 'single' | 'multi';
		default_btw_code?: string | null;
		payment_reference_template?: string | null;
	},
): Promise<unknown> {
	const res = await fetch(
		`${API_BASE}/api/exact/creditors/${encodeURIComponent(entityCode)}/${encodeURIComponent(exactId)}/defaults`,
		{
			method: 'PUT',
			credentials: 'include',
			headers: { 'content-type': 'application/json', accept: 'application/json' },
			body: JSON.stringify(body),
		},
	);
	const respBody = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`creditor defaults failed ${res.status}`), { body: respBody, status: res.status });
	return respBody;
}

export async function patchInkoopProposal(id: string, proposal: unknown): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/inkoopfacturen/${id}`, {
		method: 'PATCH',
		credentials: 'include',
		headers: { 'content-type': 'application/json', accept: 'application/json' },
		body: JSON.stringify(proposal),
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`patch failed ${res.status}`), { body, status: res.status });
	return body;
}

// Past de defaults van de gekozen crediteur toe op het voorstel (grootboek/BTW/
// betalingsreferentie) en geeft het bijgewerkte voorstel terug. Stuurt het
// huidige voorstel mee (header.exact_account_id = de gekozen crediteur) zodat
// bedrag-edits behouden blijven.
export async function applyInkoopCreditor(id: string, proposal: unknown): Promise<{ proposal: unknown }> {
	const res = await fetch(`${API_BASE}/api/inkoopfacturen/${id}/apply-creditor`, {
		method: 'POST',
		credentials: 'include',
		headers: { 'content-type': 'application/json', accept: 'application/json' },
		body: JSON.stringify(proposal),
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`apply-creditor failed ${res.status}`), { body, status: res.status });
	return body as { proposal: unknown };
}

export async function approveInkoop(id: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/inkoopfacturen/${id}/approve`, {
		method: 'POST',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`approve failed ${res.status}`), { body, status: res.status });
	return body;
}

export async function rejectInkoop(id: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/inkoopfacturen/${id}/reject`, {
		method: 'POST',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`reject failed ${res.status}`), { body, status: res.status });
	return body;
}

export async function reopenInkoop(id: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/inkoopfacturen/${id}/reopen`, {
		method: 'POST',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`reopen failed ${res.status}`), { body, status: res.status });
	return body;
}

// Goedgekeurde factuur die nooit in Exact is aangekomen opnieuw op de export-queue zetten.
export async function requeueInkoop(id: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/inkoopfacturen/${id}/requeue`, {
		method: 'POST',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`requeue failed ${res.status}`), { body, status: res.status });
	return body;
}

export async function patchVerkoopProposal(id: string, proposal: unknown): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/verkoopfacturen/${id}`, {
		method: 'PATCH',
		credentials: 'include',
		headers: { 'content-type': 'application/json', accept: 'application/json' },
		body: JSON.stringify(proposal),
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`patch failed ${res.status}`), { body, status: res.status });
	return body;
}

export async function approveVerkoop(id: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/verkoopfacturen/${id}/approve`, {
		method: 'POST',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`approve failed ${res.status}`), { body, status: res.status });
	return body;
}

export async function rejectVerkoop(id: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/verkoopfacturen/${id}/reject`, {
		method: 'POST',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`reject failed ${res.status}`), { body, status: res.status });
	return body;
}

export async function reopenVerkoop(id: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/verkoopfacturen/${id}/reopen`, {
		method: 'POST',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`reopen failed ${res.status}`), { body, status: res.status });
	return body;
}

export async function disconnectExact(entityCode: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/exact/admin/credentials/${encodeURIComponent(entityCode)}`, {
		method: 'DELETE',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`disconnectExact failed ${res.status}`), { body, status: res.status });
	return body;
}

export async function syncExact(entityCode: string): Promise<{
	ok: boolean;
	updated: { creditors: number; ledgers: number; vatCodes: number; journals: number; paymentStatusUpdated: number };
}> {
	const res = await fetch(`${API_BASE}/api/exact/admin/sync/${encodeURIComponent(entityCode)}`, {
		method: 'POST',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`syncExact failed ${res.status}`), { body, status: res.status });
	return body as {
		ok: boolean;
		updated: { creditors: number; ledgers: number; vatCodes: number; journals: number; paymentStatusUpdated: number };
	};
}

export async function putExactEntityDefaults(
	entityCode: string,
	body: {
		default_journal?: string | null;
		default_purchase_ledger?: string | null;
		default_shipping_ledger?: string | null;
		is_vat_exempt?: boolean;
	},
): Promise<{ ok: boolean; proposal_reset_count?: number }> {
	const res = await fetch(`${API_BASE}/api/exact/entity-defaults/${encodeURIComponent(entityCode)}`, {
		method: 'PUT',
		credentials: 'include',
		headers: { 'content-type': 'application/json', accept: 'application/json' },
		body: JSON.stringify(body),
	});
	const respBody = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`entity-defaults failed ${res.status}`), { body: respBody, status: res.status });
	return respBody as { ok: boolean; proposal_reset_count?: number };
}

export async function deleteEntity(id: string): Promise<unknown> {
	const res = await fetch(`${API_BASE}/api/entities/${id}`, {
		method: 'DELETE',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`deleteEntity failed ${res.status}`), { body, status: res.status });
	return body;
}

export async function uploadDocument(params: {
	file: File;
	entityCode: string;
	subject?: string;
	from?: string;
	processingMode?: 'sync' | 'batch';
	channel?: 'general' | 'inkoop' | 'verkoop';
}): Promise<{ document_id: string; status: string; reason?: string; r2_key?: string; message?: string; processing_mode?: string }> {
	const form = new FormData();
	form.append('file', params.file);
	form.append('entity_code', params.entityCode);
	if (params.subject) form.append('subject', params.subject);
	if (params.from) form.append('from', params.from);
	if (params.processingMode) form.append('processing_mode', params.processingMode);
	if (params.channel) form.append('channel', params.channel);
	const res = await fetch(`${API_BASE}/api/documents`, {
		method: 'POST',
		credentials: 'include',
		body: form,
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) {
		throw Object.assign(new Error(`Upload failed ${res.status}`), { body, status: res.status });
	}
	return body;
}

// ---------- M365 mailbox-pull beheer (admin-only) ----------

async function jsonRequest<T>(path: string, init: RequestInit): Promise<T> {
	const res = await fetch(`${API_BASE}${path}`, {
		credentials: 'include',
		headers: { 'content-type': 'application/json', accept: 'application/json' },
		...init,
	});
	const body = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(`${path} failed ${res.status}`), { body, status: res.status });
	return body as T;
}

export async function createM365App(params: {
	tenant_id: string;
	client_id: string;
	client_secret: string;
	display_name: string;
	client_secret_expires_at?: string | null;
}): Promise<{ id: string }> {
	return jsonRequest('/api/m365/apps', { method: 'POST', body: JSON.stringify(params) });
}

export async function patchM365App(
	id: string,
	patch: { client_secret?: string; display_name?: string; client_secret_expires_at?: string | null },
): Promise<unknown> {
	return jsonRequest(`/api/m365/apps/${encodeURIComponent(id)}`, {
		method: 'PATCH',
		body: JSON.stringify(patch),
	});
}

export async function deleteM365App(id: string): Promise<unknown> {
	return jsonRequest(`/api/m365/apps/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function createM365Mailbox(params: {
	m365_app_id: string;
	mailbox_address: string;
	default_entity_code: string;
	processed_folder_name: string;
	processing_mode?: 'sync' | 'batch';
	poll_interval_minutes?: number;
}): Promise<{ id: string; mailbox_user_id: string; processed_folder_id: string }> {
	return jsonRequest('/api/m365/mailboxes', { method: 'POST', body: JSON.stringify(params) });
}

export async function patchM365Mailbox(
	id: string,
	patch: {
		active?: boolean;
		processing_mode?: 'sync' | 'batch';
		poll_interval_minutes?: number;
		outgoing_folder_name?: string | null;
		outgoing_sender_patterns?: string[];
		outgoing_subject_patterns?: string[];
		outgoing_match_mode?: 'or' | 'and';
	},
): Promise<unknown> {
	return jsonRequest(`/api/m365/mailboxes/${encodeURIComponent(id)}`, {
		method: 'PATCH',
		body: JSON.stringify(patch),
	});
}

export async function deleteM365Mailbox(id: string): Promise<unknown> {
	return jsonRequest(`/api/m365/mailboxes/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function testM365Mailbox(id: string): Promise<{
	ok: boolean;
	mailbox_address?: string;
	folder_display_name?: string;
	folder_item_count?: number;
	detail?: string;
}> {
	return jsonRequest(`/api/m365/mailboxes/${encodeURIComponent(id)}/test`, { method: 'POST' });
}

export async function askDocuments(body: { question: string; top_k?: number }): Promise<{
	answer: string;
	citations: Array<{
		tag: string;
		document_id: string;
		counterparty_name: string | null;
		document_date: string | null;
		amount_total: string | null;
		currency: string | null;
		subject: string | null;
	}>;
	matched_count?: number;
	used_filter_path?: boolean;
	filters_detected?: {
		counterparty: string | null;
		date_from: string | null;
		date_to: string | null;
	} | null;
	tokens_used?: number;
}> {
	return jsonRequest('/api/documents/ask', {
		method: 'POST',
		body: JSON.stringify(body),
	});
}

export async function historicalImportExact(
	entityCode: string,
	body: { since: string; until?: string; max?: number; skip?: number },
): Promise<{
	processed: number;
	skipped_duplicate: number;
	merged_with_existing: number;
	skipped_no_attachment: number;
	more_available: boolean;
	next_skip: number;
	last_entry_date: string | null;
}> {
	return jsonRequest(`/api/exact/admin/historical-import/${encodeURIComponent(entityCode)}`, {
		method: 'POST',
		body: JSON.stringify(body),
	});
}

export async function backfillM365Mailbox(
	id: string,
	body: { since: string; max?: number; next_link?: string },
): Promise<{
	processed: number;
	more_available: boolean;
	next_link: string | null;
	last_received_at: string | null;
}> {
	return jsonRequest(`/api/m365/mailboxes/${encodeURIComponent(id)}/backfill`, {
		method: 'POST',
		body: JSON.stringify(body),
	});
}
